package com.evoapps.evoshift.domain.model

/**
 * SH-267: someone a manager of a workplace may borrow for a month: they actively work at another
 * workplace of the same owner's workplace group ([homeWorkplaceId]) and have no engagement here.
 * What the person picker needs, from the borrowCandidates function (functions/borrowing.js): their
 * answers for each day as given to their home workplace (the comments meant for it left out),
 * their planned hours this month with this owner, and the days they are away (never why: requests
 * and absences stay with the home workplace).
 */
data class BorrowCandidate(
    val userId: String,
    val displayName: String,
    val photoUrl: String?,
    val homeWorkplaceId: String,
    val homeWorkplaceName: String,
    /** Their position at home (null: none). */
    val role: String?,
    val skills: List<String>,
    /** Planned hours this month at the owner's workplaces (home and borrowed). */
    val monthHours: Double,
    /** Day of the month to their answer for it. */
    val days: Map<Int, DayPreference>,
    /** Days of the month they are away (an approved absence at home), without the reason. */
    val awayDays: Set<Int>
) {
    /**
     * The person as the picker and the grid rank them here: one engagement at [workplaceId] with
     * their home position and skills (in memory only: borrowing gives no engagement).
     */
    fun asTeamMember(workplaceId: String): User = User(
        uid = userId,
        displayName = displayName,
        photoUrl = photoUrl,
        engagements = mapOf(
            workplaceId to Engagement(
                workplaceId = workplaceId,
                cachedWorkplaceName = null,
                role = role,
                skills = skills,
                permission = UserPermission.EMPLOYEE,
                isActive = true,
                startDate = "",
                isPrimary = false
            )
        ),
        isShadow = false,
        createdAt = ""
    )
}

/**
 * SH-267: a person planned at this workplace as borrowed (workplaces/{id}/guests/{uid}, kept by
 * functions/borrowing.js): the months they have shifts here and where they are from.
 */
data class WorkplaceGuest(
    val userId: String,
    val homeWorkplaceId: String?,
    val months: List<String>
)
